import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { CheckCircle2, AlertCircle, Clock, MinusCircle, Zap, Search, FileSearch, Brain, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { PhaseNodeData, PhaseStatus } from './types';
import { getModelShortName, formatDuration } from './utils';

// Icon mapping
const iconMap = {
  zap: Zap,
  search: Search,
  fileSearch: FileSearch,
  brain: Brain,
  sparkles: Sparkles,
} as const;

const statusConfig: Record<PhaseStatus, {
  border: string;
  bg: string;
  icon: typeof CheckCircle2;
  iconColor: string;
}> = {
  completed: {
    border: 'border-green-500',
    bg: 'bg-green-50',
    icon: CheckCircle2,
    iconColor: 'text-green-600',
  },
  error: {
    border: 'border-red-500',
    bg: 'bg-red-50',
    icon: AlertCircle,
    iconColor: 'text-red-600',
  },
  skipped: {
    border: 'border-gray-300',
    bg: 'bg-gray-50',
    icon: MinusCircle,
    iconColor: 'text-gray-400',
  },
  pending: {
    border: 'border-gray-300',
    bg: 'bg-white',
    icon: Clock,
    iconColor: 'text-gray-400',
  },
};

interface PhaseNodeProps {
  data: PhaseNodeData;
}

function PhaseNodeComponent({ data }: PhaseNodeProps) {
  const config = statusConfig[data.status];
  const StatusIcon = config.icon;
  const PhaseIcon = iconMap[data.iconName as keyof typeof iconMap];
  const modelName = getModelShortName(data.model as string | undefined);

  return (
    <div
      className={cn(
        'px-3 py-2 rounded-lg border-2 w-[180px] cursor-pointer transition-all',
        config.border,
        config.bg,
        data.isSelected && 'ring-2 ring-blue-500 ring-offset-2'
      )}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-slate-400 !w-2 !h-2"
      />

      <div className="flex items-center gap-2 mb-1">
        <PhaseIcon className={cn('h-3.5 w-3.5', data.iconColor)} />
        <span className="text-xs font-bold text-muted-foreground">
          Phase {data.phaseNumber}
        </span>
        <StatusIcon className={cn('h-3 w-3 ml-auto', config.iconColor)} />
      </div>

      <div className="font-medium text-sm text-foreground">{data.phaseName}</div>

      <div className="flex items-center justify-between mt-2 text-xs gap-2">
        {modelName && (
          <Badge variant="secondary" className="text-[10px] truncate max-w-[120px]">
            {modelName}
          </Badge>
        )}
        <span className="text-muted-foreground whitespace-nowrap">
          {formatDuration(data.durationMs)}
        </span>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-slate-400 !w-2 !h-2"
      />
    </div>
  );
}

export const PhaseNode = memo(PhaseNodeComponent);
